import { Roles } from '@/entities/roles'
import { TopupStatus } from '@/entities/topups'
import { TopupStatusIcons, TopupStatusTitles } from '@/entities/topups/model/constans'
import { DisputModel } from '@/features/orders'
import DisputIcon from '@/shared/assets/icons/dashboard/orders.svg?react'
import { BaseButton } from '@/shared/components'
import { DealIcon } from '@/shared/components/icons/DealIcon'
import { ItemLayout } from '@/shared/components/ItemLayout/ItemLayout'
import { formatCurrency } from '@/shared/lib/currency'
import { useIsMobile } from '@/shared/utils/useIsMobile/useIsMobile'
import { TopupModel, TopupModelMerchant } from '../../model'
import { useManualConfirmTimer } from '../../model/hooks/useManualConfirmTimer'
import { DisputAmounts } from './DisputAmounts'
import { DisputLink } from './DisputLink'
import { PendingTime } from './PendingTime'
import { CopyBlock } from '@/shared/components/CopyBlock/CopyBlock'

type Props = {
  data: TopupModel | TopupModelMerchant
  onConfirm?: (id: string) => void
  userRole: Roles | null
  openModal: () => void
  buttonIsVisible: boolean
  disputId: string
  disputData: DisputModel
}

const titleClasses = 'text-[20px] md:text-[32px] md:leading-[40px]'
const subtitleClasses = 'text-text-secondary text-sm md:leading-[18px]'

export const DealHeader = ({
  data,
  onConfirm,
  userRole,
  openModal,
  buttonIsVisible,
  disputId,
  disputData,
}: Props) => {
  const isMobile = useIsMobile()

  const title = TopupStatusTitles[data.status]
  const iconStatus = TopupStatusIcons[data.status]

  const timeAgo =
    data.confirmedAt && data.confirmedAt !== '0001-01-01T00:00:00'
      ? data.confirmedAt
      : data.createdAt

  const isPending = data.status === TopupStatus.WaitingPayment
  const hasDisput = disputId && disputData

  // Хук для таймера ручного подтверждения
  const { timeRemaining, isManualConfirmAvailable } = useManualConfirmTimer({ data, userRole })

  const rubAmount = data.amountFrom
  const usdtAmount = data.amountTo

  const rubFactAmount = 'factAmountFrom' in data && data.factAmountFrom ? data.factAmountFrom : null
  const usdtFactAmount = 'factAmountTo' in data && data.factAmountTo ? data.factAmountTo : null

  const DealAmounts = () =>
    userRole === Roles.Trader ? (
      <div className="flex flex-col items-end">
        <CopyBlock text={data.amountTo?.toFixed(2)} size="base" className="text-[24px]">
          {formatCurrency(data.amountTo, data.currencyTo)}
        </CopyBlock>

        <CopyBlock text={data.amountFrom?.toFixed(2)} color="secondary">
          {formatCurrency(data.amountFrom, data.currencyFrom)}
        </CopyBlock>
      </div>
    ) : (
      <div className="flex flex-col items-end">
        <CopyBlock text={data.amountTo?.toFixed(2)} size="base" className="text-[32px]">
          {formatCurrency(usdtFactAmount ?? usdtAmount, data.currencyTo)}
        </CopyBlock>

        {rubFactAmount && (
          <CopyBlock text={rubFactAmount?.toFixed(2)} color="secondary">
            Сумма поступления: {formatCurrency(rubFactAmount, data.currencyFrom)}
          </CopyBlock>
        )}
        {rubAmount && (
          <CopyBlock text={rubAmount?.toFixed(2)} color="secondary">
            Сумма поступления: {formatCurrency(rubAmount, data.currencyFrom)}
          </CopyBlock>
        )}
      </div>
    )

  const ConfirmButton = () =>
    userRole !== Roles.Merchant &&
    isPending && (
      <BaseButton radius="lg" className="self-end" onClick={() => onConfirm?.(data.id ?? '')}>
        Подтвердить
      </BaseButton>
    )

  const DisputButton = () =>
    userRole === Roles.Merchant &&
    buttonIsVisible && (
      <BaseButton
        radius="lg"
        className="self-end"
        icon={<DisputIcon />}
        iconPosition="right"
        onClick={openModal}
      >
        Создать спор
      </BaseButton>
    )

  const ManualConfirmButton = () =>
    isManualConfirmAvailable() && (
      <BaseButton radius="lg" className="self-end" onClick={() => onConfirm?.(data.id ?? '')}>
        <span>Подтвердить</span>
        <span className="text-primary-dark-disabled"> • {timeRemaining} мин</span>
      </BaseButton>
    )

  return (
    <div className="md:flex md:flex-col gap-4 mb-4 md:mb-8 pt-7 md:pt-0 md:pb-6 md:border-b">
      <div className="md:flex md:justify-between">
        <div className="flex flex-col md:items-center gap-4">
          {isMobile && isPending && <PendingTime timeAgo={timeAgo} />}

          <ItemLayout
            icon={
              <DealIcon status={iconStatus} className="w-[40px] md:w-[52px] h-[40px] md:h-[52px]" />
            }
            title={title}
            subtitle={
              <div className="flex flex-col">
                <CopyBlock text={data?.id} title={data?.id} color="secondary">
                  ID: {data?.id}
                </CopyBlock>

                {'payerId' in data && (
                  <CopyBlock
                    text={data?.payerId}
                    title={data?.payerId}
                    color="secondary"
                    className="text-xs"
                  >
                    ID payer: {data?.payerId}
                  </CopyBlock>
                )}

                {'merchantTopupId' in data && (
                  <CopyBlock
                    text={data?.merchantTopupId}
                    title={data?.merchantTopupId}
                    color="secondary"
                    className="text-xs"
                  >
                    ID merchant topup: {data?.merchantTopupId}
                  </CopyBlock>
                )}
              </div>
            }
            titleClasses={titleClasses}
            subtitleClasses={subtitleClasses}
          />

          {isMobile && hasDisput && <DisputLink disputId={disputId} disputData={disputData} />}
        </div>

        {!isMobile && (
          <div className="flex gap-6">
            <DealAmounts />
            {hasDisput && <DisputAmounts disputData={disputData} topupData={data} />}
          </div>
        )}
      </div>

      {!isMobile && (
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-6">
            {isPending && <PendingTime timeAgo={timeAgo} />}
            {hasDisput && <DisputLink disputId={disputId} disputData={disputData} />}
          </div>

          <ConfirmButton />
          <DisputButton />
          <ManualConfirmButton />
        </div>
      )}
    </div>
  )
}
